/**
 * Панель массовых действий над выбранными чатами.
 *
 * Появляется только когда что-то выбрано: постоянная панель занимала бы место
 * в узкой колонке ради действия, которое совершают редко.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { TrashIcon } from './icons.tsx';

interface BulkBarProps {
  selectedCount: number;
  totalCount: number;
  busy: boolean;
  canDelete: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onArchive: () => void;
  onStatus: (status: string) => void;
  onDelete: () => void;
}

const STATUSES = [
  { value: 'NEW', label: 'Новый' },
  { value: 'IN_PROGRESS', label: 'В работе' },
  { value: 'WAITING', label: 'Ожидание' },
  { value: 'DONE', label: 'Завершён' },
];

export function BulkBar({
  selectedCount,
  totalCount,
  busy,
  canDelete,
  onSelectAll,
  onClear,
  onArchive,
  onStatus,
  onDelete,
}: BulkBarProps) {
  const [status, setStatus] = useState('IN_PROGRESS');

  return (
    <div className="flex flex-none flex-col gap-2 border-t border-border bg-card p-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium">Выбрано: {selectedCount}</span>

        <button
          type="button"
          className="text-brand transition-colors hover:underline disabled:opacity-50"
          onClick={onSelectAll}
          disabled={busy || selectedCount === totalCount}
        >
          Выбрать все
        </button>

        <button
          type="button"
          className="text-muted-foreground transition-colors hover:underline disabled:opacity-50"
          onClick={onClear}
          disabled={busy}
        >
          Снять выбор
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Button size="sm" variant="outline" onClick={onArchive} disabled={busy}>
          В архив
        </Button>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          disabled={busy}
          className="h-8 rounded-lg border border-border bg-background px-2 text-xs outline-none focus:border-ring"
        >
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <Button size="sm" variant="outline" onClick={() => onStatus(status)} disabled={busy}>
          Проставить
        </Button>

        {/* Удаление доступно только администратору: право telegram.chat.delete
            требует уровня FULL и никому больше не выдано (§27 ТЗ). */}
        {canDelete && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto text-destructive hover:bg-destructive/15 hover:text-destructive"
            onClick={onDelete}
            disabled={busy}
          >
            <TrashIcon className="size-4" />
            Удалить
          </Button>
        )}
      </div>
    </div>
  );
}
